import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import {
    Button,
    ButtonProps,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    styled,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import Box from "@mui/material/Box";
import React from "react";
import { useTranslation } from "react-i18next";

import { useHttpClient } from "../../../../services/mortgage-client/MortgageClientContext";
import GroupLifeInsuranceText from "./GroupLifeInsuranceText";

const DialogWrapper = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogTitle-root": {
        marginTop: "var(--lukb-portal-safe-area-inset-top, 0px)",
        marginBottom: "24px",
        padding: "16px",
        paddingRight: "60px",
        marginLeft: "var(--lukb-portal-safe-area-inset-left, 0px)",
        marginRight: "var(--lukb-portal-safe-area-inset-right, 0px)",
    },
    "& .MuiDialogContent-root": {
        paddingLeft: "16px",
        paddingRight: "14px",
        paddingTop: "0px",
        marginLeft: "var(--lukb-portal-safe-area-inset-left, 0px)",
        marginRight: "var(--lukb-portal-safe-area-inset-right, 0px)",
    },
    "& .MuiDialogActions-root": {
        padding: "16px",
        paddingTop: "28px",
        marginBottom: "var(--lukb-portal-safe-area-inset-bottom, 0px)",
        marginLeft: "var(--lukb-portal-safe-area-inset-left, 0px)",
        marginRight: "var(--lukb-portal-safe-area-inset-right, 0px)",
    },

    [theme.breakpoints.up("lg")]: {
        "& .MuiDialog-paper": {
            width: "100%",
            maxWidth: "900px",
            margin: "0px",
            paddingLeft: "0px",
            paddingRight: "0px",
        },
        "& .MuiDialogTitle-root": {
            paddingTop: "40px",
            marginBottom: "40px",
            textAlign: "center",
            marginTop: "0px",
            marginLeft: "0px",
            marginRight: "0px",
        },
        "& .MuiDialogContent-root": {
            paddingLeft: "60px",
            paddingRight: "24px",
            marginLeft: "0px",
            marginRight: "0px",
        },
        "& .MuiDialogActions-root": {
            padding: "8px",
            paddingBottom: "40px",
            paddingTop: "48px",
            marginBottom: "0px",
            marginLeft: "0px",
            marginRight: "0px",
        },
    },
}));

export const TitleTypography = styled(Typography)(({ theme }) => ({
    fontWeight: 500,
    fontSize: "21px",
    textAlign: "left",
    [theme.breakpoints.up("lg")]: {
        fontWeight: 300,
        fontSize: "48px",
        textAlign: "center",
    },
    lineHeight: "30px",
}));

export const ImageWrapper = styled(Box)(({ theme }) => ({
    [theme.breakpoints.up("lg")]: {
        marginTop: "10px",
        marginBottom: "32px",
        marginRight: "45px",
    },
}));

export const CloseIconButton = styled(Button)(({ theme }) => ({
    paddingTop: "0px",
    color: "#333333",
    [theme.breakpoints.up("lg")]: {
        top: "24px",
        right: "24px",
    },
}));

export const CloseButton = styled(Button)<ButtonProps>(({ theme }) => ({
    fontSize: "16px",
    fontWeight: 500,
    backgroundColor: "#0093D1",
    borderColor: "#0093D1",
    borderRadius: "4px",
    boxShadow: "none",
    textTransform: "none",
    "&:hover": {
        backgroundColor: "#0093D1",
        borderColor: "#0093D1",
        boxShadow: "none",
    },
    "&:active": {
        boxShadow: "none",
        backgroundColor: "#0093D1",
        borderColor: "#0093D1",
    },
    height: "36px",
    [theme.breakpoints.up("lg")]: {
        height: "inherit",
        marginRight: "52px",
    },
}));

export const InsuranceCompanyParagraph = styled(Box)(({ theme }) => ({
    width: "303px",
    [theme.breakpoints.up("lg")]: {
        width: "100%",
    },
}));

export const Paragraph = styled(Box)(({ theme }) => ({
    paddingBottom: "32px",
    [theme.breakpoints.up("lg")]: {
        paddingRight: "140px",
    },
}));

const LifeInsurancePopupScreen = (props: { open: boolean; closeDialogFunction: () => void }) => {
    const { open, closeDialogFunction } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const httpClient = useHttpClient();
    const desktop = useMediaQuery(theme.breakpoints.up("lg"));
    const mobile = useMediaQuery(theme.breakpoints.down("lg"));

    if (open) {
        document.body.style.overflowY = "hidden";
    } else {
        document.body.style.overflowY = "unset";
    }

    const firstList = [
        "SummaryScreen.LifeInsurance.Uncomplicated insurance conclusion for homeowners",
        "SummaryScreen.LifeInsurance.Favorable conditions thanks to group insurance",
        "SummaryScreen.LifeInsurance.Freely available sum insured in case of emergency",
    ];

    const secondList = [
        "SummaryScreen.LifeInsurance.The life of the mortgage debtor is insured",

        "SummaryScreen.LifeInsurance.If there are several mortgage debtors (joint and several debtors), the insurance can be taken out for each debtor individually",
    ];

    const thirdList = [
        "SummaryScreen.LifeInsurance.Any joint debtors",
        "SummaryScreen.LifeInsurance.Spouse / registered partner",
        "SummaryScreen.LifeInsurance.Children",
        "SummaryScreen.LifeInsurance.Other heirs, excluding a community of heirs",
    ];

    const forthList = [
        "SummaryScreen.LifeInsurance.The property is an owner-occupied home or holiday home in Switzerland",
        "SummaryScreen.LifeInsurance.You are between 18 and 55 years old",
        "SummaryScreen.LifeInsurance.The mortgage is with LUKB",
    ];

    const fifthList = [
        "SummaryScreen.LifeInsurance.in the first year until the end of the current calendar year",
        "SummaryScreen.LifeInsurance.in the following years, automatic extension by one year on payment of the premium no later than 31 March.",
        "SummaryScreen.LifeInsurance.The insurance policy can be cancelled each year at the end of the calendar year",
        "SummaryScreen.LifeInsurance.Insurance cover applies",
    ];

    const sublist = [
        "SummaryScreen.LifeInsurance.up to a maximum age of 60",
        "SummaryScreen.LifeInsurance.only for as long as the mortgage is with Luzerner Kantonalbank",
        "SummaryScreen.LifeInsurance.the insured person resides in Switzerland",
    ];

    return (
        <DialogWrapper
            scroll={"body"}
            onClose={closeDialogFunction}
            open={open}
            data-cy={"life-insurance-pop-up"}
            fullScreen={mobile}>
            <DialogTitle align={"center"} sx={{ position: "relative" }}>
                <TitleTypography>{t("SummaryScreen.LifeInsurance.Group life insurance")}</TitleTypography>
                <CloseIconButton
                    sx={{
                        position: "absolute",
                        top: "12px",
                        right: "14px",
                        margin: "0px",
                        padding: "0px",
                        minWidth: "0px",
                    }}
                    aria-label={t("Actions.Close")}
                    onClick={closeDialogFunction}
                    data-cy={"close-icon-button"}>
                    <CloseOutlinedIcon sx={{ width: "32px", height: "32px" }} />
                </CloseIconButton>
            </DialogTitle>

            <DialogContent>
                <Paragraph>
                    <Typography variant={"body1"}>
                        {t("SummaryScreen.LifeInsurance.With a life insurance policy")}
                    </Typography>
                </Paragraph>

                <GroupLifeInsuranceText
                    valueOfSubtitle={"SummaryScreen.LifeInsurance.Your advantages"}
                    listValues={firstList}
                />
                <GroupLifeInsuranceText
                    valueOfSubtitle={"SummaryScreen.LifeInsurance.Insured person"}
                    listValues={secondList}
                />
                <GroupLifeInsuranceText
                    valueOfSubtitle={"SummaryScreen.LifeInsurance.Benefit"}
                    listValues={thirdList}
                    paragraphValues={["SummaryScreen.LifeInsurance.The order of the beneficiaries is fixed:"]}
                />
                <GroupLifeInsuranceText
                    valueOfSubtitle={"SummaryScreen.LifeInsurance.Services"}
                    paragraphValues={["SummaryScreen.LifeInsurance.If an insured person dies"]}
                />
                <GroupLifeInsuranceText
                    valueOfSubtitle={"SummaryScreen.LifeInsurance.Requirements"}
                    listValues={forthList}
                />
                <GroupLifeInsuranceText
                    valueOfSubtitle={"SummaryScreen.LifeInsurance.Insurance period"}
                    listValues={fifthList}
                    listItemWithSublist={"SummaryScreen.LifeInsurance.Insurance cover applies"}
                    sublistValues={sublist}
                />
                <GroupLifeInsuranceText
                    valueOfSubtitle={"SummaryScreen.LifeInsurance.Costs"}
                    paragraphValues={[
                        "SummaryScreen.LifeInsurance.The annual premium is 0.135% of the insured sum per insured person. It is guaranteed for 10 years.",
                        "SummaryScreen.LifeInsurance.In the following graphic, you will see an example of a calculation (with a mortgage of CHF 500,000) for one or two insured persons:",
                    ]}
                />
                {desktop && (
                    <ImageWrapper>
                        <img
                            style={{
                                width: "100%",
                                objectFit: "initial",
                            }}
                            src={httpClient.buildUrl("icons/life-insurance-graphic-large.svg")}
                        />
                    </ImageWrapper>
                )}
                {mobile && (
                    <img
                        style={{ width: "100%", maxWidth: "500px", marginBottom: "40px", marginTop: "8px" }}
                        src={httpClient.buildUrl("icons/life-insurance-graphic-mobile-large.png")}
                    />
                )}
                <Box sx={{ marginBottom: "16px" }}>
                    <Typography variant={"subtitle1"} sx={{ textAlign: "justify" }}>
                        {t("SummaryScreen.LifeInsurance.Insurance company")}
                    </Typography>
                </Box>
                <InsuranceCompanyParagraph>
                    <Typography variant={"body1"}>
                        {t("SummaryScreen.LifeInsurance.Swiss Mobiliar Life Insurance Company Ltd.")}
                    </Typography>
                </InsuranceCompanyParagraph>
            </DialogContent>

            <DialogActions>
                <CloseButton variant="contained" onClick={closeDialogFunction} data-cy={"close-button"}>
                    {t("Actions.Close")}
                </CloseButton>
            </DialogActions>
        </DialogWrapper>
    );
};
export default LifeInsurancePopupScreen;
